interface Props {
  slug: string;
  author: string;
  summary: string;
  slugError?: string | null;
  /** Blog base path from tenant featureConfig (e.g. "/legal-insights"). */
  blogBasePath?: string;
  onSlugChange: (next: string) => void;
  onAuthorChange: (next: string) => void;
  onSummaryChange: (next: string) => void;
}

/**
 * Slug + author + summary panel. Slug shows the live URL preview so
 * authors can see what publishing produces; the on-disk uniqueness
 * check still runs server-side at save time.
 */
export function DetailsPanel({
  slug,
  author,
  summary,
  slugError,
  blogBasePath = "/legal-insights",
  onSlugChange,
  onAuthorChange,
  onSummaryChange,
}: Props) {
  return (
    <div className="shadow-card rounded-lg bg-paper-hi">
      <div className="hair-b border-b px-4 py-3">
        <h3 className="text-[13px] font-semibold">Details</h3>
      </div>
      <div className="space-y-3 p-4">
        <Field label="Slug" hint={slug ? `${blogBasePath}/${slug}` : `${blogBasePath}/---`} error={slugError}>
          <input
            type="text"
            value={slug}
            onChange={(e) => onSlugChange(e.target.value)}
            placeholder="my-post-title"
            className={`hair w-full rounded-md border bg-paper px-2.5 py-1.5 font-mono text-[12.5px] text-ink-2 focus:outline-none ${
              slugError ? "border-rust focus:border-rust" : "focus:border-tide"
            }`}
          />
        </Field>

        <Field label="Author">
          <input
            type="text"
            value={author}
            onChange={(e) => onAuthorChange(e.target.value)}
            placeholder="Jane Doe"
            className="hair w-full rounded-md border bg-paper px-2.5 py-1.5 text-[12.5px] text-ink-2 focus:border-tide focus:outline-none"
          />
        </Field>

        <Field label="Summary" hint="Shown in listings and meta descriptions.">
          <textarea
            value={summary}
            onChange={(e) => onSummaryChange(e.target.value)}
            rows={3}
            placeholder="A short teaser…"
            className="hair w-full rounded-md border bg-paper px-2.5 py-1.5 text-[12.5px] leading-relaxed text-ink-2 focus:border-tide focus:outline-none"
          />
        </Field>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-ink-4">
        {label}
      </label>
      {children}
      {error ? (
        <div className="mt-1 text-[11px] text-rust">{error}</div>
      ) : hint ? (
        <div className="mt-1 truncate text-[11px] text-ink-5">{hint}</div>
      ) : null}
    </div>
  );
}
